import { Suspense, lazy, useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import styled from 'styled-components';

import { pagePaths } from '@/modules/defines/paths';
import IntroContainer from '@/containers/IntroContainer';

import { useQuery } from '@tanstack/react-query';
import { spaceAPI } from './api';
import { getSpacesResponse } from './modules/defines/interface';
import XRNaviContainer from './containers/XRNaviContainer';
import { calculateDistance } from './utils/function/util';
import { buildGraph } from '@/utils/function/dijkstra';
import useNavigationStore from '@/store/navigation';
import LoadingModal from './components/modal/LoadingModal';
import NaviContainer from './containers/NaviContainer';

const MainContainer = lazy(() => import('@/containers/MainContainer'));

const ParkingContainer = lazy(() => import('./containers/ParkingContainer'));

export function HydrateFallback() {
  return <div>Loading...</div>;
}
interface RouterProps {
  data: getSpacesResponse | undefined;
  isLoading: boolean;
}

const getNumberValue = (...values: unknown[]) => {
  for (const value of values) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && numberValue > 0) {
      return numberValue;
    }
  }
  return undefined;
};

const getForwardExternalSpaceId = (panorama: any) =>
  getNumberValue(
    panorama?.PanoramaSelf?.external_public_space_id,
    panorama?.space_id,
    panorama?.spaceId,
    panorama?.PanoramaSelf?.external_space_id
  );

const getReverseExternalSpaceId = (panorama: any) =>
  getNumberValue(
    panorama?.PanoramaSelf?.space_id,
    panorama?.space_id,
    panorama?.spaceId
  );

const routerBasename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

const router = ({ data, isLoading }: RouterProps) =>
  createBrowserRouter(
    [
      {
        // #000 Splash 페이지
        path: pagePaths.intro,
        element: <IntroContainer />,
      },
      {
        // #001 메인 페이지
        path: pagePaths.main,
        element: (
          <Suspense
            fallback={<LoadingModal isLoading={true} text="Loading..." />}
          >
            <MainContainer spaces={data?.spaces} isLoading={isLoading} />
          </Suspense>
        ),
      },
      // {
      //   path: pagePaths.direction,
      //   element: (
      //     <DirectionContainer
      //       spaceData={data?.spaces}
      //       walkData={data?.walkways}
      //       isLoading={isLoading}
      //     />
      //   ),
      // },
      {
        // #002 길찾기 페이지
        path: pagePaths.navi,
        element: (
          <NaviContainer
            spaceData={data?.spaces}
            walkData={data?.walkways}
            isLoading={isLoading}
          />
        ),
      },
      {
        // #004 주차장 페이지
        path: pagePaths.parking,
        element: (
          <Suspense
            fallback={<LoadingModal isLoading={true} text="Loading..." />}
          >
            <ParkingContainer />
          </Suspense>
        ),
      },
      {
        path: pagePaths.xrnavi,
        element: (
          <Suspense fallback={<div>Loading...</div>}>
            <XRNaviContainer />
          </Suspense>
        ),
      },
      { path: '*', element: <Navigate to={pagePaths.intro} replace /> },
    ],
    { basename: routerBasename }
  );

const RouterComponent = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['getAllSpace'],
    queryFn: () =>
      spaceAPI.getAllSpace({
        code: import.meta.env.VITE_SECRET_API_KEY,
      }),
    staleTime: 1000 * 60 * 5,
    enabled: true,
  });

  const setGraph = useNavigationStore((state) => state.setGraph);

  const routerInstance = useMemo(() => {
    data?.data.walkways.forEach((space: any) => {
      // space.pano의 위치 정보를 미리 Map에 저장하여 빠르게 접근할 수 있도록 설정
      const panoMap = new Map<string, { x: number; y: number }>();
      space.pano.forEach((p: any) => panoMap.set(p.id, { x: p.x, y: p.y }));

      space.pano.forEach((panorama: any) => {
        const blockedIdList = [] as number[];
        const connectedList = [] as string[];
        const externalList = [] as string[];

        if (panorama.panoramaself1 != null) {
          panorama.panoramaself1.forEach((temp: any) => {
            if (temp['PanoramaSelf']['type'] === 'block') {
              blockedIdList.push(temp['id']);
            } else if (temp['PanoramaSelf']['type'] === 'connect') {
              connectedList.push(
                `${temp['id']}_${temp['PanoramaSelf']['dir_1']}_${temp['PanoramaSelf']['weight']}`
              );
            } else {
              const externalSpaceId = getForwardExternalSpaceId(temp);
              if (!externalSpaceId) return;
              externalList.push(
                `${externalSpaceId}_${temp['id']}_${temp['PanoramaSelf']['dir_1']}_${temp['PanoramaSelf']['external_space_title']}_${temp['PanoramaSelf']['weight']}`
              );
            }
          });
        }
        if (panorama.panoramaself2 != null) {
          panorama.panoramaself2.forEach((temp: any) => {
            if (temp['PanoramaSelf']['type'] === 'block') {
              blockedIdList.push(temp['id']);
            } else if (temp['PanoramaSelf']['type'] === 'connect') {
              connectedList.push(
                `${temp['id']}_${temp['PanoramaSelf']['dir_2']}_${temp['PanoramaSelf']['weight']}`
              );
            } else {
              const externalSpaceId = getReverseExternalSpaceId(temp);
              if (!externalSpaceId) return;
              externalList.push(
                `${externalSpaceId}_${temp['id']}_${temp['PanoramaSelf']['dir_2']}_${temp['PanoramaSelf']['space_title']}_${temp['PanoramaSelf']['weight']}`
              );
            }
          });
        }

        panorama.blockedIdList = blockedIdList;
        panorama.connectedList = connectedList;
        panorama.externalList = externalList;

        const connectedNode: {
          id: any;
          distance: string | number;
          type: string;
        }[] = [];
        const viewDistance = Math.sqrt(panorama.view_distance);

        // 공간 내 모든 panorama를 반복하여 거리 기반 연결을 설정
        panoMap.forEach((other, otherId) => {
          if (
            panorama.id === otherId ||
            blockedIdList.includes(Number(otherId))
          )
            return;

          const distance = calculateDistance(
            panorama.x,
            panorama.y,
            other.x,
            other.y
          );

          if (distance <= viewDistance) {
            connectedNode.push({
              id: otherId,
              distance: distance,
              type: 'normal',
            });
          }
        });

        // 기존 connectedList 및 externalList의 데이터를 connectedNode에 추가
        connectedList.forEach((connect) => {
          const [id, , weight] = connect.split('_');
          connectedNode.push({
            id: parseInt(id),
            distance: parseFloat(weight),
            type: 'connnect',
          });
        });

        externalList.forEach((external) => {
          const externalData = external.split('_');
          const [space_id, id] = externalData;
          const weight = externalData[externalData.length - 1];
          const external_space = data?.data.walkways.find(
            (temp_space: any) => temp_space.id === parseInt(space_id)
          );
          if (!external_space) return;

          const arrive_panorama = external_space.pano.find(
            (point: any) => point.id === parseInt(id)
          );
          if (!arrive_panorama) return;

          let external_type = '';
          if (arrive_panorama.category === panorama.category) {
            if (panorama.category === 'elevator') {
              external_type = 'external_elevator';
            } else if (panorama.category === 'escalator') {
              external_type = 'external_escalator';
            } else {
              external_type = 'external';
            }
          } else {
            external_type = 'external';
          }

          connectedNode.push({
            id: parseInt(id),
            distance: parseFloat(weight),
            type: external_type,
          });
        });

        panorama.connectedNode = connectedNode;
        panorama.viewDistanceSquared = panorama.view_distance;
      });
    });

    return router({
      data: data?.data,
      isLoading: isLoading,
    });
  }, [data, isLoading]);

  useEffect(() => {
    if (data) {
      const graph = buildGraph(data?.data.walkways);
      setGraph(graph);
    }
  }, [data, setGraph]);

  return (
    <ContentWrapper>
      <RouterProvider router={routerInstance} />
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0px;
  padding: 0px;
  width: 100%;
  height: calc(var(--dynamic-height, 100dvh));
  overflow: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
  body.isDesktop & {
    width: 496px;
    max-width: 90%; /* 화면이 좁아질 때 유연하게 축소 */
    margin: 0 auto;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }
`;

export default RouterComponent;
